import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { useSocket } from '../../../../hooks/useSocket';
import { Clock, Users, Activity, Building2, RefreshCw, ChevronDown, User, Coffee, Utensils, AlertCircle, DollarSign, Timer, TrendingUp, Zap, Award, Target } from 'lucide-react';
import { formatUtcTimeOnly } from '../../../../utils/timezoneUtils';

const BirdsEye = () => {
  const { currentUser } = useAuth();
  const token = localStorage.getItem('token');
  const { socket } = useSocket(currentUser?.id);
  const [clockedInUsers, setClockedInUsers] = useState([]);
  const [groupedUsers, setGroupedUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [activities, setActivities] = useState([]);
  const [error, setError] = useState(null);
  const [idleUsers, setIdleUsers] = useState([]);
  const [groupingMode, setGroupingMode] = useState('Activity');
  const [showDropdown, setShowDropdown] = useState(false);
  const [hoveredEmployee, setHoveredEmployee] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const dropdownRef = useRef(null);

  const fetchClockedInUsers = async () => {
    if (!token) {
      setError('No authentication token available');
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const response = await fetch('/api/labor/active', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication required. Please log in again.');
        }
        if (response.status === 403) {
          throw new Error('You do not have permission to view this data.');
        }
        throw new Error(`Failed to fetch active users: ${response.status}`);
      }

      const data = await response.json();
      setClockedInUsers(data.usersList || []);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching clocked in users:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchActivities = async () => {
    if (!token) return;

    try {
      const response = await fetch('/api/activities', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.warn('Failed to fetch activities, using fallback');
        return;
      }

      const data = await response.json();
      setActivities(data);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  useEffect(() => {
    if (token && currentUser) {
      fetchClockedInUsers();
      fetchActivities();

      const interval = setInterval(() => {
        if (token) {
          fetchClockedInUsers();
        }
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [token, currentUser]);

  useEffect(() => {
    if (socket && socket.on) {
      const handleClockUpdate = () => {
        if (token) {
          fetchClockedInUsers();
        }
      };

      socket.on('update-clock-status', handleClockUpdate);
      socket.on('team-update', handleClockUpdate);

      return () => {
        socket.off('update-clock-status', handleClockUpdate);
        socket.off('team-update', handleClockUpdate);
      };
    }
  }, [socket, token]);

  const getGroupingKey = (user, mode) => {
    switch (mode) {
      case 'Activity':
        return user.activity || 'No Activity';
      case 'Business Unit':
        return user.business_unit || 'No Business Unit';
      case 'Group':
        return user.group || 'No Group';
      case 'Project':
        return user.project || 'No Project';
      default:
        return user.activity || 'No Activity';
    }
  };

  const sortUsersInGroup = (users, mode) => {
    return users.sort((a, b) => {
      if (mode === 'Business Unit') {
        if (a.activity !== b.activity) {
          return (a.activity || '').localeCompare(b.activity || '');
        }
      }
      return (a.full_name || '').localeCompare(b.full_name || '');
    });
  };

  const filterUsersForMode = (users, mode) => {
    if (mode === 'Project') {
      return users.filter(user => user.project && user.project.trim() !== '');
    }
    return users;
  };

  useEffect(() => {
    const filteredUsers = filterUsersForMode(clockedInUsers, groupingMode);
    
    const grouped = filteredUsers.reduce((acc, user) => {
      const groupKey = getGroupingKey(user, groupingMode);
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(user);
      return acc;
    }, {});

    Object.keys(grouped).forEach(key => {
      grouped[key] = sortUsersInGroup(grouped[key], groupingMode);
    });

    setGroupedUsers(grouped);

    const now = new Date();
    const idle = clockedInUsers.filter(user => {
      const isIdle = user.activity === 'IDLE' || user.business_unit === 'IDLE';
      if (!isIdle) return false;
      
      const clockInTime = new Date(user.clock_in_time);
      const minutesElapsed = (now.getTime() - clockInTime.getTime()) / (1000 * 60);
      return minutesElapsed >= 5;
    });
    setIdleUsers(idle);
  }, [clockedInUsers, groupingMode]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getStatusColor = (clockInTime) => {
    if (!clockInTime) return 'bg-gradient-to-r from-slate-500 to-slate-600';
    const now = new Date();
    const clockIn = new Date(clockInTime);
    const hoursWorked = (now - clockIn) / (1000 * 60 * 60);
    
    if (hoursWorked < 4) return 'bg-gradient-to-r from-emerald-500 to-green-500';
    if (hoursWorked < 8) return 'bg-gradient-to-r from-amber-500 to-yellow-500';
    if (hoursWorked < 10) return 'bg-gradient-to-r from-orange-500 to-amber-500';
    return 'bg-gradient-to-r from-red-500 to-rose-500';
  };

  const getActivityIcon = (activity) => {
    const activityLower = (activity || '').toLowerCase();
    if (activityLower.includes('break')) return Coffee;
    if (activityLower.includes('lunch')) return Utensils;
    if (activityLower === 'idle') return AlertCircle;
    return Activity;
  };

  const formatClockTime = (dateTime) => {
    if (!dateTime) return 'N/A';
    return formatUtcTimeOnly(dateTime, 'America/Chicago').replace(/\s[A-Z]{2,4}$/, '');
  };

  const getTimeDuration = (clockInTime) => {
    if (!clockInTime) return '0h 0m';
    
    const startDate = new Date(clockInTime);
    const now = new Date();
    const diff = now.getTime() - startDate.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const getActivityInfo = (activityName) => {
    const activity = activities.find(a => a.name === activityName);
    return activity || { name: activityName, is_billable: false };
  };

  const handleRefresh = () => {
    if (token && currentUser) {
      setLoading(true);
      fetchClockedInUsers();
    }
  };

  // Calculate statistics
  const stats = {
    total: clockedInUsers.length,
    onBreak: clockedInUsers.filter(u => u.activity === 'BREAK' || u.activity === 'LUNCH').length,
    overtime: clockedInUsers.filter(u => {
      const duration = getTimeDuration(u.clock_in_time);
      return duration.includes('10h') || duration.includes('11h') || duration.includes('12h');
    }).length,
    billable: clockedInUsers.filter(u => {
      const activity = activities.find(a => a.name === u.activity);
      return activity?.is_billable;
    }).length
  };

  if (!token || !currentUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-400">Waiting for authentication...</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-400">Loading active users...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
        <div className="flex flex-col items-center justify-center h-64">
          <div className="text-red-400 mb-4">{error}</div>
          <button
            onClick={() => {
              if (token && currentUser) {
                setLoading(true);
                setError(null);
                fetchClockedInUsers();
              }
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Modern Header with Stats */}
      <div className="bg-slate-900/50 backdrop-blur-sm border-b border-slate-700/50 sticky top-0 z-40">
        <div className="px-6 py-4">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl">
                <Activity className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Operations Dashboard</h1>
                <p className="text-sm text-slate-400">Real-time employee monitoring</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-all duration-200 border border-slate-600"
                >
                  <span className="text-sm">Group by:</span>
                  <span className="font-semibold">{groupingMode}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showDropdown && (
                  <div className="absolute top-full right-0 mt-2 bg-slate-800 border border-slate-600 rounded-lg shadow-2xl overflow-hidden min-w-[160px]">
                    {['Activity', 'Business Unit', 'Group', 'Project'].map((mode) => (
                      <button
                        key={mode}
                        onClick={() => {
                          setGroupingMode(mode);
                          setShowDropdown(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 hover:bg-slate-700 transition-colors ${
                          groupingMode === mode ? 'bg-slate-700 text-cyan-400' : 'text-slate-200'
                        }`}
                      >
                        {mode}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <button
                onClick={handleRefresh}
                className="p-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-all duration-200 border border-slate-600"
                title="Refresh"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
              
              <div className="text-sm text-slate-400">
                Last updated: {formatUtcTimeOnly(lastUpdated).replace(/\s[A-Z]{2,4}$/, '')}
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 border border-blue-500/30 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-400 text-sm mb-1">Total Active</p>
                  <p className="text-3xl font-bold text-white">{stats.total}</p>
                </div>
                <Users className="w-8 h-8 text-blue-400 opacity-50" />
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-emerald-600/20 to-green-600/20 border border-emerald-500/30 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-400 text-sm mb-1">Billable</p>
                  <p className="text-3xl font-bold text-white">{stats.billable}</p>
                </div>
                <DollarSign className="w-8 h-8 text-emerald-400 opacity-50" />
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-amber-600/20 to-yellow-600/20 border border-amber-500/30 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-400 text-sm mb-1">On Break</p>
                  <p className="text-3xl font-bold text-white">{stats.onBreak}</p>
                </div>
                <Coffee className="w-8 h-8 text-amber-400 opacity-50" />
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-red-600/20 to-rose-600/20 border border-red-500/30 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-400 text-sm mb-1">Overtime</p>
                  <p className="text-3xl font-bold text-white">{stats.overtime}</p>
                </div>
                <Clock className="w-8 h-8 text-red-400 opacity-50" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="p-6">
        {/* Idle Users Alert Section */}
        {idleUsers.length > 0 && (
          <div className="mb-6">
            <div className="bg-gradient-to-r from-red-900/30 to-rose-900/30 border border-red-500/50 rounded-2xl p-6 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-red-600/10 to-transparent animate-pulse"></div>
              
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-red-500/20 rounded-lg">
                    <AlertCircle className="w-6 h-6 text-red-400" />
                  </div>
                  <h2 className="text-xl font-bold text-red-400">
                    Attention Required - {idleUsers.length} Idle Employee{idleUsers.length > 1 ? 's' : ''}
                  </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {idleUsers.map((user) => (
                    <div
                      key={user.id}
                      className="bg-slate-800/50 backdrop-blur-sm border border-red-500/30 rounded-xl p-4 hover:bg-slate-800/70 transition-all duration-200"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-semibold text-white">{user.full_name}</h3>
                          <p className="text-xs text-slate-400">ID: {user.employee_id}</p>
                        </div>
                        <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-lg font-medium">
                          IDLE
                        </span>
                      </div>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-500">Duration:</span>
                          <span className="text-red-400 font-medium">{getTimeDuration(user.clock_in_time)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Since:</span>
                          <span className="text-slate-300">{formatClockTime(user.clock_in_time)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Groups Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
          {Object.entries(groupedUsers)
            .sort((a, b) => b[1].length - a[1].length)
            .map(([groupName, users]) => {
              const groupInfo = getActivityInfo(groupName);
              const isExpanded = selectedGroup === groupName;
              
              return (
                <div
                  key={groupName}
                  className={`bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden hover:border-slate-600 transition-all duration-300 ${
                    isExpanded ? 'lg:col-span-2 xl:col-span-2' : ''
                  }`}
                >
                  {/* Group Header */}
                  <div 
                    className={`px-5 py-4 border-b border-slate-700/50 cursor-pointer hover:bg-slate-700/20 transition-colors ${
                      groupInfo.is_billable ? 'bg-gradient-to-r from-emerald-900/20 to-transparent' : ''
                    }`}
                    onClick={() => setSelectedGroup(isExpanded ? null : groupName)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${
                          groupInfo.is_billable ? 'bg-emerald-400 animate-pulse' : 'bg-slate-400'
                        }`}></div>
                        <h3 className="font-semibold text-white text-lg">{groupName}</h3>
                        {groupInfo.is_billable && (
                          <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full font-medium">
                            BILLABLE
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-3 py-1 bg-slate-700/50 text-slate-300 rounded-lg text-sm font-medium">
                          {users.length} {users.length === 1 ? 'person' : 'people'}
                        </span>
                        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </div>
                    </div>
                  </div>

                  {/* Employee List */}
                  <div className={`p-4 ${isExpanded ? 'max-h-96' : 'max-h-64'} overflow-y-auto custom-scrollbar`}>
                    <div className={`grid ${isExpanded ? 'grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'} gap-2`}>
                      {users.map((user) => {
                        const duration = getTimeDuration(user.clock_in_time);
                        const isOvertime = duration.includes('10h') || duration.includes('11h') || duration.includes('12h');
                        const isOnBreak = user.activity === 'BREAK' || user.activity === 'LUNCH';
                        
                        return (
                          <div
                            key={user.id}
                            className={`relative group bg-slate-900/50 border rounded-xl p-3 hover:bg-slate-900/70 transition-all duration-200 cursor-pointer ${
                              isOvertime ? 'border-red-500/50 bg-red-900/10' : 
                              isOnBreak ? 'border-amber-500/50 bg-amber-900/10' :
                              'border-slate-700'
                            }`}
                            onMouseEnter={() => setHoveredEmployee(user.id)}
                            onMouseLeave={() => setHoveredEmployee(null)}
                          >
                            <div className="flex items-center gap-3">
                              {/* Avatar with status */}
                              <div className="relative">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center text-white font-semibold text-sm">
                                  {user.full_name?.substring(0, 2).toUpperCase()}
                                </div>
                                <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-slate-900 ${
                                  isOvertime ? 'bg-red-500' : 
                                  isOnBreak ? 'bg-amber-500' :
                                  'bg-emerald-500'
                                }`}></div>
                              </div>
                              
                              {/* Info */}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-white truncate">{user.full_name}</p>
                                <p className="text-xs text-slate-400">{duration}</p>
                              </div>

                              {/* Status Icon */}
                              {(isOvertime || isOnBreak) && (
                                <div className="flex-shrink-0">
                                  {isOvertime && <Clock className="w-4 h-4 text-red-400" />}
                                  {isOnBreak && <Coffee className="w-4 h-4 text-amber-400" />}
                                </div>
                              )}
                            </div>

                            {/* Detailed Tooltip */}
                            {hoveredEmployee === user.id && (
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-slate-900 border border-slate-600 rounded-xl p-3 shadow-2xl z-50 w-56 pointer-events-none">
                                <div className="space-y-2">
                                  <div>
                                    <p className="font-semibold text-white">{user.full_name}</p>
                                    <p className="text-xs text-slate-400">Employee ID: {user.employee_id}</p>
                                  </div>
                                  
                                  <div className="space-y-1 text-xs">
                                    <div className="flex justify-between">
                                      <span className="text-slate-500">Clock In:</span>
                                      <span className="text-slate-300">{formatClockTime(user.clock_in_time)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-slate-500">Duration:</span>
                                      <span className={`font-medium ${isOvertime ? 'text-red-400' : 'text-slate-300'}`}>
                                        {duration}
                                      </span>
                                    </div>
                                    {groupingMode !== 'Activity' && user.activity && (
                                      <div className="flex justify-between">
                                        <span className="text-slate-500">Activity:</span>
                                        <span className="text-slate-300">{user.activity}</span>
                                      </div>
                                    )}
                                    {groupingMode !== 'Business Unit' && user.business_unit && (
                                      <div className="flex justify-between">
                                        <span className="text-slate-500">Unit:</span>
                                        <span className="text-slate-300">{user.business_unit}</span>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {isOvertime && (
                                    <div className="pt-2 border-t border-slate-700">
                                      <p className="text-xs text-red-400 font-medium flex items-center gap-1">
                                        <AlertCircle className="w-3 h-3" />
                                        Overtime Alert
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>

        {/* Empty State */}
        {clockedInUsers.length === 0 && (
          <div className="flex flex-col items-center justify-center h-96">
            <div className="p-4 bg-slate-800/30 rounded-full mb-4">
              <Users className="w-16 h-16 text-slate-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-400 mb-2">No Active Employees</h3>
            <p className="text-slate-500">There are currently no employees clocked in</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BirdsEye;

// Add custom styles
const style = document.createElement('style');
style.textContent = `
  /* Custom scrollbar */
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-track {
    background: rgba(30, 41, 59, 0.5);
    border-radius: 3px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(100, 116, 139, 0.5);
    border-radius: 3px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(100, 116, 139, 0.7);
  }
  
  /* Smooth animations */
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  /* Pulse animation for billable indicator */
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
  
  .animate-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }
`;

if (!document.head.querySelector('style[data-birds-eye-redesign]')) {
  style.setAttribute('data-birds-eye-redesign', '');
  document.head.appendChild(style);
}